// RunStatusTables.jsx
import React from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from '@mui/material';
import { motion } from 'framer-motion';
import PropTypes from 'prop-types';
import DecisionBreakdownRow from './StatusDecisionNode';
import { useThemeContext } from '../Context/ThemeContext';

const statusChipPalette = (status = '') => {
  const s = String(status).toUpperCase();
  switch (s) {
    case 'SUCCESS':
      return { bgLight: '#D1FAE5', text: '#065F46', border: '#10B981' };
    case 'FAILED':
    case 'ERROR':
      return { bgLight: '#FEE2E2', text: '#991B1B', border: '#EF4444' };
    case 'PROCESSING':
    case 'ACTIVE':
    case 'RUNNING':
    case 'IN_PROGRESS':
      return { bgLight: '#DBEAFE', text: '#1E40AF', border: '#3B82F6' };
    case 'PARTIAL_SUCCESS':
      return { bgLight: '#FEF3C7', text: '#92400E', border: '#F59E0B' };
    default:
      return { bgLight: '#F3F4F6', text: '#6B7280', border: '#D1D5DB' };
  }
};
const getStatusColor = (status) => {
  switch ((status || '').toUpperCase()) {
    case 'SUCCESS':
      return '#065F46';
    case 'PROCESSING':
      return '#1E40AF';
    case 'FAILED':
    case 'ERROR':
      return '#991B1B';
    case 'PARTIAL_SUCCESS':
      return '#a74a10ff';
    default:
      return '#6B7280';
  }
};

const rowVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: (index) => ({
    opacity: 1,
    y: 0,
    transition: { delay: index * 0.05, duration: 0.3, ease: 'easeOut' },
  }),
  hover: {
    scale: 1.01,
    backgroundColor: 'rgba(79, 172, 254, 0.05)',
    transition: { duration: 0.2, ease: 'easeInOut' },
  },
};

const fmtDuration = (n) =>
  typeof n === 'number' ? `${parseFloat((n || 0.01).toFixed(2))} sec` : '';

// const STAGES = ['extract', 'transform', 'load'];
// Note: 'backtrack' is included in orderedStageKeys but will only be displayed
// if it exists in the workflow run data (steps_by_phase or steps_by_entity).
// The filter functions below ensure backtrack only appears when present.
const orderedStageKeys = [
  'extract',
  'transform',
  'decision',
  'load',
  'backtrack',
];

// Only includes stages that exist in the data - backtrack will be filtered out
// if it doesn't exist in stepsByPhase
const getStagesFromPhase = (stepsByPhase = {}) =>
  orderedStageKeys.filter((k) => stepsByPhase[k]);

// Only includes stages that exist in the data - backtrack will be filtered out
// if it doesn't exist in entityGroup
const getStagesFromEntity = (entityGroup = {}) =>
  orderedStageKeys.filter((k) => entityGroup[k]);

/**
 * PhaseStatusTable — your original “Step Status by Phase” table,
 * cleaned up and kept as a reusable sub-component.
 * Toggle via <RunStatusTables showPhase={false} />
 */
export const PhaseStatusTable = ({ runStatus, logsDialogOpen }) => {
  const { darkMode } = useThemeContext();
  const stepsByPhase = runStatus?.steps_by_phase || {};
  const stages = getStagesFromPhase(stepsByPhase);

  return (
    <Box
      component={motion.div}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      sx={{
        borderRadius: '8px',
        background: darkMode
          ? 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)'
          : 'linear-gradient(135deg, #ffffff 0%, #f8faff 100%)',
        border: `1px solid ${darkMode ? 'rgba(89, 101, 166, 0.3)' : 'rgba(89, 101, 166, 0.2)'}`,
        p: 3,
        mt: 3,
        mb: 2,
        position: 'relative',
        overflow: 'hidden',
        boxShadow: darkMode
          ? `
            0px 4px 12px rgba(89, 101, 166, 0.15),
            0px 8px 24px rgba(0, 0, 0, 0.3),
            0px 1px 3px rgba(0, 0, 0, 0.2)
          `
          : `
            0px 4px 12px rgba(89, 101, 166, 0.08),
            0px 8px 24px rgba(0, 0, 0, 0.04),
            0px 1px 3px rgba(0, 0, 0, 0.06)
          `,
        '&:before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          background: darkMode
            ? 'linear-gradient(135deg, rgba(89,101,166,0.08) 0%, rgba(89,101,166,0.04) 100%)'
            : 'linear-gradient(135deg, rgba(89,101,166,0.04) 0%, rgba(89,101,166,0.02) 100%)',
          zIndex: 0,
        },
      }}
    >
      <Box sx={{ mb: 2, position: 'relative', zIndex: 1 }}>
        <Typography
          sx={{
            fontSize: '0.875rem',
            fontWeight: 600,
            color: darkMode ? '#E2E8F0' : '#1a202c',
          }}
        >
          Step Status by Phase
        </Typography>
        {/* If needed:
        {runStatus?.workflow_run_id && (
          <Typography sx={{ fontSize: '0.875rem', color: '#64748b' }}>
            Workflow Run ID: {runStatus.workflow_run_id}
          </Typography>
        )} */}
      </Box>

      <TableContainer
        sx={{
          borderRadius: '12px',
          overflow: 'hidden',
          border: `1px solid ${darkMode ? 'rgba(89, 101, 166, 0.2)' : 'rgba(89, 101, 166, 0.15)'}`,
          position: 'relative',
          zIndex: 1,
        }}
      >
        <Table size="small">
          <TableHead
            sx={{
              position: 'relative',
              fontSize: '0.875rem',
              background: darkMode
                ? 'linear-gradient(135deg, rgba(89,101,166,0.2) 0%, rgba(89,101,166,0.15) 100%)'
                : 'linear-gradient(135deg, rgba(89,101,166,0.1) 0%, rgba(89,101,166,0.08) 100%)',
              boxShadow: darkMode
                ? 'inset 0 -1px 0 rgba(89,101,166,0.3), 0 1px 6px rgba(0,0,0,0.1)'
                : 'inset 0 -1px 0 rgba(89,101,166,0.2), 0 1px 6px rgba(0,0,0,0.05)',
              '& th': {
                color: darkMode ? '#E2E8F0' : '#374151',
                fontWeight: 600,
                transition: 'all 0.2s ease',
              },
              '& th:not(:last-of-type)': {
                borderRight: `1px solid ${darkMode ? 'rgba(89,101,166,0.3)' : 'rgba(89,101,166,0.2)'}`,
              },
              '&:hover th': {
                color: darkMode ? '#ffffff' : '#1f2937',
              },
            }}
          >
            <TableRow>
              {[
                'Stage',
                'Success Count',
                'Failure Count',
                'Status',
                'Duration',
              ].map((label) => (
                <TableCell
                  key={label}
                  sx={{
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    py: 2,
                    color: darkMode ? '#E2E8F0' : '#374151',
                  }}
                >
                  {label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {stages.map((stageKey, idx) => {
              const step = stepsByPhase?.[stageKey] || {};
              const status = step?.step_status || 'UNKNOWN';
              const successRecords = Number.isFinite(step?.success_record_count)
                ? step.success_record_count
                : '';
              const failureRecords = Number.isFinite(step?.failure_record_count)
                ? step.failure_record_count
                : '';
              // Handle duration display: show N/A for PENDING status or null duration
              const duration = (() => {
                const statusUpper = String(status || '').toUpperCase();
                const isPending =
                  statusUpper === 'PENDING' || statusUpper === 'NOT_STARTED';
                const hasDuration = Number.isFinite(step?.duration_seconds);

                if (isPending && !hasDuration) {
                  return 'N/A';
                }
                if (hasDuration) {
                  return fmtDuration(step.duration_seconds);
                }
                return 'N/A';
              })();

              return (
                <React.Fragment key={`${stageKey}-${idx}`}>
                  <TableRow
                    component={motion.tr}
                    custom={idx}
                    variants={rowVariants}
                    initial="hidden"
                    animate="visible"
                    whileHover="hover"
                    sx={{
                      fontSize: '0.875rem',
                      backgroundColor: darkMode ? 'transparent' : 'transparent',
                      '&:nth-of-type(even)': {
                        backgroundColor: darkMode
                          ? 'rgba(89, 101, 166, 0.05)'
                          : 'rgba(89, 101, 166, 0.03)',
                      },
                      '&:hover': {
                        backgroundColor: darkMode
                          ? 'rgba(89, 101, 166, 0.1)'
                          : 'rgba(89, 101, 166, 0.05)',
                        transition: 'background-color 0.2s ease',
                      },
                    }}
                  >
                    {/* STAGE */}
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1.25}>
                        <Box
                          sx={{
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            bgcolor: getStatusColor(status),
                            boxShadow: '0 0 0 3px rgba(79,172,254,0.08)',
                          }}
                        />
                        <Typography
                          fontSize={13}
                          fontWeight={600}
                          sx={{ color: darkMode ? '#E2E8F0' : '#1f2937' }}
                        >
                          {String(stageKey || '').toUpperCase()}
                        </Typography>
                      </Box>
                    </TableCell>

                    {/* SUCCESS */}
                    <TableCell
                      sx={{ fontSize: 13, color: '#065f46', fontWeight: 600 }}
                    >
                      {successRecords}
                    </TableCell>

                    {/* FAILURE */}
                    <TableCell
                      sx={{ fontSize: 13, color: '#991b1b', fontWeight: 600 }}
                    >
                      {failureRecords}
                    </TableCell>

                    {/* STATUS */}
                    <TableCell
                      sx={{ cursor: 'pointer' }}
                      onClick={() => logsDialogOpen?.()}
                    >
                      <Chip
                        label={<Box component="span">{status}</Box>}
                        size="small"
                        sx={{
                          backgroundColor: statusChipPalette(status).bgLight,
                          color: statusChipPalette(status).text,
                          border: `1px solid ${statusChipPalette(status).border}`,

                          fontWeight: 700,
                          height: 24,
                          borderRadius: '10px',
                          px: 1.2,
                          boxShadow:
                            'inset 0 1px 0 rgba(255,255,255,0.25), 0 1px 2px rgba(2,8,23,0.08)',
                        }}
                      />
                    </TableCell>

                    {/* DURATION */}
                    <TableCell
                      sx={{
                        fontSize: 13,
                        color: darkMode
                          ? 'rgba(226, 232, 240, 0.8)'
                          : '#374151',
                        fontStyle: duration === 'N/A' ? 'italic' : 'normal',
                        opacity: duration === 'N/A' ? 0.7 : 1,
                      }}
                    >
                      {duration}
                    </TableCell>
                  </TableRow>

                  {/* Decision routing breakdown row */}
                  {stageKey === 'decision' && (
                    <DecisionBreakdownRow step={step} colSpan={5} />
                  )}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

PhaseStatusTable.propTypes = {
  runStatus: PropTypes.object,
  logsDialogOpen: PropTypes.func,
};

/**
 * EntityStatusTables — renders one table per entity.
 */
export const EntityStatusTables = ({ runStatus, logsDialogOpen }) => {
  const { darkMode } = useThemeContext();
  const stepsByEntity = Array.isArray(runStatus?.steps_by_entity)
    ? runStatus.steps_by_entity
    : [];

  if (!stepsByEntity.length) return null;

  return (
    <Box sx={{ mt: 4 }}>
      <Typography
        sx={{
          fontSize: '0.875rem',
          fontWeight: 700,
          color: darkMode ? '#E2E8F0' : '#1a202c',
          mb: 1,
        }}
      >
        Step Status by Entity
      </Typography>

      {stepsByEntity.map((group, entityIdx) => {
        const entityName = group?.entity || `Entity ${entityIdx + 1}`;
        const badgeStatus =
          group?.backtrack?.step_status ||
          group?.load?.step_status ||
          group?.transform?.step_status ||
          group?.decision?.step_status ||
          group?.extract?.step_status ||
          'UNKNOWN';

        const stages = getStagesFromEntity(group);

        return (
          <Box
            key={`${entityName}-${entityIdx}`}
            component={motion.div}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            sx={{
              borderRadius: '8px',
              background: darkMode
                ? 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)'
                : 'linear-gradient(135deg, #ffffff 0%, #f9faff 100%)',
              border: `1px solid ${darkMode ? 'rgba(89, 101, 166, 0.3)' : 'rgba(89, 101, 166, 0.2)'}`,
              p: 2,
              mb: 2,
              position: 'relative',
              overflow: 'hidden',
              boxShadow: darkMode
                ? `
                  0px 4px 12px rgba(89, 101, 166, 0.15),
                  0px 8px 24px rgba(0, 0, 0, 0.3),
                  0px 1px 3px rgba(0, 0, 0, 0.2)
                `
                : `
                  0px 4px 12px rgba(89, 101, 166, 0.12),
                  0px 8px 24px rgba(89, 101, 166, 0.08),
                  0px 1px 3px rgba(89, 101, 166, 0.15)
                `,
              '&:before': {
                content: '""',
                position: 'absolute',
                inset: 0,
                background: darkMode
                  ? 'linear-gradient(135deg, rgba(89,101,166,0.08) 0%, rgba(89,101,166,0.04) 100%)'
                  : 'linear-gradient(135deg, rgba(89,101,166,0.04) 0%, rgba(89,101,166,0.02) 100%)',
                zIndex: 0,
              },
            }}
          >
            {/* Header */}
            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  bgcolor: getStatusColor(badgeStatus),
                  boxShadow: '0 0 0 3px rgba(79,172,254,0.08)',
                }}
              />
              <Typography
                sx={{
                  fontSize: '0.95rem',
                  fontWeight: 700,
                  color: darkMode ? '#E2E8F0' : '#111827',
                }}
              >
                {entityName}
              </Typography>
            </Box>

            <TableContainer
              sx={{
                borderRadius: '12px',
                overflow: 'hidden',
                border: `1px solid ${darkMode ? 'rgba(89, 101, 166, 0.2)' : 'rgba(89, 101, 166, 0.15)'}`,
                position: 'relative',
                zIndex: 1,
              }}
            >
              <Table size="small">
                <TableHead
                  sx={{
                    position: 'relative',
                    background: darkMode
                      ? 'linear-gradient(135deg, rgba(89,101,166,0.2) 0%, rgba(89,101,166,0.15) 100%)'
                      : 'linear-gradient(135deg, rgba(89,101,166,0.1) 0%, rgba(89,101,166,0.08) 100%)',
                    boxShadow: darkMode
                      ? 'inset 0 -1px 0 rgba(89,101,166,0.3), 0 1px 6px rgba(0,0,0,0.1)'
                      : 'inset 0 -1px 0 rgba(89,101,166,0.2), 0 1px 6px rgba(0,0,0,0.05)',
                    transition:
                      'background .25s ease, box-shadow .25s ease, transform .15s ease',

                    // header text default
                    '& th': {
                      color: darkMode ? '#E2E8F0' : '#1f2230',
                      fontWeight: 600,
                      transition:
                        'transform .2s ease, letter-spacing .2s ease, color .2s ease',
                    },

                    // separators with theme tint
                    '& th:not(:last-of-type)': {
                      borderRight: `1px solid ${darkMode ? 'rgba(89,101,166,0.3)' : 'rgba(89,101,166,0.2)'}`,
                    },

                    '&:hover th': {
                      color: darkMode ? '#ffffff' : '#111827',
                    },
                  }}
                >
                  <TableRow>
                    {[
                      'Stage',
                      'Success Count',
                      'Failure Count',
                      'Status',
                      'Duration',
                    ].map((label) => (
                      <TableCell
                        key={label}
                        sx={{
                          fontSize: '0.875rem',
                          fontWeight: 600,
                          py: 0.6,
                          color: darkMode ? '#E2E8F0' : '#374151',
                        }}
                      >
                        {label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {stages.map((stageKey, idx) => {
                    const step = group?.[stageKey] || {};
                    const status = step?.step_status || 'UNKNOWN';
                    const successRecords = Number.isFinite(
                      step?.success_record_count,
                    )
                      ? step.success_record_count
                      : '';
                    const failureRecords = Number.isFinite(
                      step?.failure_record_count,
                    )
                      ? step.failure_record_count
                      : '';
                    // Handle duration display: show N/A for PENDING status or null duration
                    const duration = (() => {
                      const statusUpper = String(status || '').toUpperCase();
                      const isPending =
                        statusUpper === 'PENDING' ||
                        statusUpper === 'NOT_STARTED';
                      const hasDuration = Number.isFinite(
                        step?.duration_seconds,
                      );

                      if (isPending && !hasDuration) {
                        return 'N/A';
                      }
                      if (hasDuration) {
                        return fmtDuration(step.duration_seconds);
                      }
                      return 'N/A';
                    })();

                    return (
                      <React.Fragment key={`${entityName}-${stageKey}-${idx}`}>
                        <TableRow
                          component={motion.tr}
                          custom={idx}
                          variants={rowVariants}
                          initial="hidden"
                          animate="visible"
                          whileHover="hover"
                          sx={{
                            fontSize: '0.875rem',
                            backgroundColor: darkMode
                              ? 'transparent'
                              : 'transparent',
                            '&:nth-of-type(even)': {
                              backgroundColor: darkMode
                                ? 'rgba(89, 101, 166, 0.05)'
                                : 'rgba(89, 101, 166, 0.03)',
                            },
                            '&:hover': {
                              backgroundColor: darkMode
                                ? 'rgba(89, 101, 166, 0.1)'
                                : 'rgba(89, 101, 166, 0.05)',
                              transition: 'background-color 0.2s ease',
                            },
                          }}
                        >
                          {/* STAGE */}
                          <TableCell>
                            <Box display="flex" alignItems="center" gap={1.25}>
                              <Box
                                sx={{
                                  width: 10,
                                  height: 10,
                                  borderRadius: '50%',
                                  bgcolor: getStatusColor(status),
                                  boxShadow: '0 0 0 3px rgba(79,172,254,0.08)',
                                }}
                              />
                              <Typography
                                fontSize={13}
                                fontWeight={600}
                                sx={{ color: darkMode ? '#E2E8F0' : '#1f2937' }}
                              >
                                {String(stageKey || '').toUpperCase()}
                              </Typography>
                            </Box>
                          </TableCell>

                          {/* SUCCESS */}
                          <TableCell
                            sx={{
                              fontSize: 13,
                              color: '#065f46',
                              fontWeight: 600,
                            }}
                          >
                            {successRecords}
                          </TableCell>

                          {/* FAILURE */}
                          <TableCell
                            sx={{
                              fontSize: 13,
                              color: '#991b1b',
                              fontWeight: 600,
                            }}
                          >
                            {failureRecords}
                          </TableCell>

                          {/* STATUS */}
                          <TableCell
                            sx={{ cursor: 'pointer' }}
                            onClick={() => logsDialogOpen?.()}
                          >
                            <Chip
                              label={<Box component="span">{status}</Box>}
                              size="small"
                              sx={{
                                backgroundColor:
                                  statusChipPalette(status).bgLight,
                                color: statusChipPalette(status).text,
                                border: `1px solid ${statusChipPalette(status).border}`,

                                fontWeight: 650,
                                height: 24,
                                borderRadius: '10px',
                                px: 1.2,
                                boxShadow:
                                  'inset 0 1px 0 rgba(255,255,255,0.25), 0 1px 2px rgba(2,8,23,0.08)',
                              }}
                            />
                          </TableCell>

                          {/* DURATION */}
                          <TableCell
                            sx={{
                              fontSize: 13,
                              color: darkMode
                                ? 'rgba(226, 232, 240, 0.8)'
                                : '#374151',
                              fontStyle:
                                duration === 'N/A' ? 'italic' : 'normal',
                              opacity: duration === 'N/A' ? 0.7 : 1,
                            }}
                          >
                            {duration}
                          </TableCell>
                        </TableRow>

                        {/* Decision routing breakdown row */}
                        {stageKey === 'decision' && (
                          <DecisionBreakdownRow step={step} colSpan={5} />
                        )}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        );
      })}
    </Box>
  );
};

EntityStatusTables.propTypes = {
  runStatus: PropTypes.object,
  logsDialogOpen: PropTypes.func,
};

/**
 * Wrapper that intelligently renders Phase or Entity tables based on entity count.
 * - Single entity: shows PhaseStatusTable (from steps_by_phase)
 * - Multiple entities: shows EntityStatusTables (from steps_by_entity)
 */
const RunStatusTables = ({ runStatus, logsDialogOpen, showPhase = false }) => {
  const stepsByEntity = Array.isArray(runStatus?.steps_by_entity)
    ? runStatus.steps_by_entity
    : [];
  const entityCount = stepsByEntity.length;
  const hasStepsByPhase =
    runStatus?.steps_by_phase &&
    Object.keys(runStatus.steps_by_phase).length > 0;

  // If showPhase is explicitly true, show phase table (for RunStatusTable.jsx usage)
  if (showPhase) {
    return (
      <>
        {hasStepsByPhase && (
          <PhaseStatusTable
            runStatus={runStatus}
            logsDialogOpen={logsDialogOpen}
          />
        )}
        {entityCount > 0 && (
          <EntityStatusTables
            runStatus={runStatus}
            logsDialogOpen={logsDialogOpen}
          />
        )}
      </>
    );
  }

  // Auto-detect: single entity -> phase table, multiple entities -> entity tables
  if (entityCount === 1 && hasStepsByPhase) {
    // Single entity: show phase-based table
    return (
      <PhaseStatusTable runStatus={runStatus} logsDialogOpen={logsDialogOpen} />
    );
  } else if (entityCount > 1) {
    // Multiple entities: show entity-based tables
    return (
      <EntityStatusTables
        runStatus={runStatus}
        logsDialogOpen={logsDialogOpen}
      />
    );
  } else if (hasStepsByPhase) {
    // Fallback: if no entities but phase data exists, show phase table
    return (
      <PhaseStatusTable runStatus={runStatus} logsDialogOpen={logsDialogOpen} />
    );
  }

  // No data to display
  return null;
};

RunStatusTables.propTypes = {
  runStatus: PropTypes.object,
  logsDialogOpen: PropTypes.func,
  showPhase: PropTypes.bool,
};

export default RunStatusTables;
